import {
  isResponsiveProp,
  ResponsiveProp,
} from "@faire/web--source/slate/Theme/ResponsiveProp";

export type DynamicVariable<T extends `--f-${string}`> = {
  variable: T;
  className: `${string}[${T}]`;
};

export type DynamicResponsiveVariables<T extends `--f-${string}`> = {
  mobileAndAbove: DynamicVariable<T>;
  tabletAndAbove?: DynamicVariable<
    `${T}-md` extends `--f-${string}` ? `${T}-md` : never
  >;
  desktopAndAbove?: DynamicVariable<
    `${T}-lg` extends `--f-${string}` ? `${T}-lg` : never
  >;
  xLargeAndAbove?: DynamicVariable<
    `${T}-xl` extends `--f-${string}` ? `${T}-xl` : never
  >;
  xxLargeDesktop?: DynamicVariable<
    `${T}-2xl` extends `--f-${string}` ? `${T}-2xl` : never
  >;
};

type Styles = {
  [cssVarName: string]: string;
};

function getVarName(variable: string): string {
  const matches = variable.match(/^var\((.*)\)$/);
  if (matches) {
    return matches[1] ?? variable;
  }

  return variable;
}

export function assignInlineVars(
  vars: Record<string, string | undefined | null>
): Styles {
  const styles: Styles = {};
  for (const varName in vars) {
    const value = vars[varName];

    if (value == null) {
      continue;
    }

    styles[getVarName(varName)] = value;
  }
  return styles;
}

export function mapResponsivePropToCSSVars(
  responsiveProp: ResponsiveProp<string>,
  cssVars: DynamicResponsiveVariables<`--f-${string}`>
) {
  // assign CSS vars
  const vars: Record<string, string | undefined> = {};
  const classes: string[] = [];
  if (isResponsiveProp(responsiveProp)) {
    vars[cssVars.mobileAndAbove.variable] = responsiveProp.mobileAndAbove;
    classes.push(cssVars.mobileAndAbove.className);
    if (cssVars.tabletAndAbove && responsiveProp.tabletAndAbove) {
      vars[cssVars.tabletAndAbove.variable] = responsiveProp.tabletAndAbove;
      classes.push(cssVars.tabletAndAbove.className);
    }
    if (cssVars.desktopAndAbove && responsiveProp.desktopAndAbove) {
      vars[cssVars.desktopAndAbove.variable] = responsiveProp.desktopAndAbove;
      classes.push(cssVars.desktopAndAbove.className);
    }
    if (cssVars.xLargeAndAbove && responsiveProp.xLargeAndAbove) {
      vars[cssVars.xLargeAndAbove.variable] = responsiveProp.xLargeAndAbove;
      classes.push(cssVars.xLargeAndAbove.className);
    }
    if (cssVars.xxLargeDesktop && responsiveProp.xxLargeDesktop) {
      vars[cssVars.xxLargeDesktop.variable] = responsiveProp.xxLargeDesktop;
      classes.push(cssVars.xxLargeDesktop.className);
    }
  } else {
    vars[cssVars.mobileAndAbove.variable] = responsiveProp;
    classes.push(cssVars.mobileAndAbove.className);
  }

  return { vars, classes };
}
